import { quoteFormSelector, transactionPayloadSelector, quoteIdSelector, verticalMediaContentSelector } from 'ctm-selectors';

export * from 'ctm-selectors/selectors/state';

/**
 * OccupancyType selector - get the occupancyType from state
 * @param {Object} state - The app state
 * @returns {String|null} - The current value for occupancyType or null if it hasn't been set yet
 */
export const occupancyTypeSelector = (state) => {
    return quoteFormSelector(state).getIn(['occupancy', 'occupancyType']);
};

/**
 * coverTypeSelector selector - get the coverTypeSelector from state
 * @param {Object} state - The app state
 * @returns {String|null} - The current value for coverTypeSelector or null if it hasn't been set yet
 */
export const coverTypeSelector = (state) => {
    return quoteFormSelector(state).getIn(['coverDetails', 'coverType']);
};

/**
 * shieldedThankYouHeroImageSelector selector - get the hero image for Shielded Thank you page from state
 * @param {Object} state - The app state
 * @returns {String|null} - The current value for shieldedThankYouHeroImageSelector or null if no value required
 */
export const shieldedThankYouHeroImageSelector = (state) => {
    return verticalMediaContentSelector(state).getIn(['shielded_thank_you_hero_image_url']);
};

export const yearBuiltFlooredTo2010 = ({ state }) => {
    return Math.max(quoteFormSelector(state).getIn(['property', 'yearBuilt']), 2010) || 2010;
};

/**
 * Return Home or Property based on the occupancyType
 * @param {Object} state - The app state
 * @returns {String} - The name for the property
 */
export const homeOrProperty = ({ state }) => {
    const names = {
        LANDLORD: 'Property',
        OWNER: 'Home',
        TENANT: 'Home'
    };
    return names[occupancyTypeSelector(state)];
};

/**
 * Return Home or Building based on the occupancyType
 * @param {Object} state - The app state
 * @returns {String} - The name for the property
 */
export const homeOrBuilding = ({ state }) => {
    const names = {
        LANDLORD: 'Building',
        OWNER: 'Home',
        TENANT: 'Home'
    };
    return names[occupancyTypeSelector(state)];
};

/**
 * Return Landlord for any coverTypeSelector if occupancyType is landlord. Otherwise, return Home, Contents or Home & Contents
 * based on the selected coverTypeSelector. This is used on the Previous Cover page.
 * @param {Object} state - The app state
 * @returns {String} - The insurance type
 */
export const insuranceType = ({ state }) => {
    const categories = {
        LANDLORD: {
            H: 'landlord',
            C: 'landlord',
            HC: 'landlord'
        },
        OWNER: {
            H: 'home',
            C: 'contents',
            HC: 'home and contents'
        },
        TENANT: {
            H: 'home',
            C: 'contents',
            HC: 'home and contents'
        }
    };
    const occupancyType = occupancyTypeSelector(state);
    const coverType = coverTypeSelector(state);
    return occupancyType ? categories[occupancyType][coverType] : '';
};

/**
 * Return Landlord for any coverTypeSelector if occupancyType is landlord. Otherwise, return Home, Contents or Home & Contents
 * based on the selected coverTypeSelector. This is used on the Previous Cover page.
 * @param {Object} args - The args
 * @param {Object} args.state - The app state
 * @returns {String} - The insurance type
 */
export const previousInsuranceType = ({ state }) => {
    const categories = {
        LANDLORD: 'landlord or home and/or contents',
        OWNER: 'home and/or contents',
        TENANT: 'home and/or contents'
    };
    const occupancyType = occupancyTypeSelector(state);
    return occupancyType ? categories[occupancyType] : '';
};

/**
 * ProductCategory selector - get a full ProductCategory name instead of using the acronym stored in state.
 * The full name is different depending on the occupancyTypeSelector. Defaults to occupanyType='owner' if a value for occupancyTypeSelector
 * has not yet been set
 * @param {Object} args - The args
 * @param {Object} args.state - The app state
 * @param {String} args.value - The default value to use in the full productCategory lookup if a value for productCategory
 * has not yet been set on state
 * @returns {String} - The full name of the productCategory
 */
export const productCategory = ({ state, value }) => {
    const categories = {
        LANDLORD: {
            H: 'Building',
            C: 'Contents',
            HC: 'Building & Contents'
        },
        OWNER: {
            H: 'Home',
            C: 'Contents',
            HC: 'Home & Contents'
        },
        TENANT: {
            H: 'Home',
            C: 'Contents',
            HC: 'Home & Contents'
        }
    };
    const coverType = coverTypeSelector(state);
    const occupancyType = occupancyTypeSelector(state);

    if (occupancyType && coverType && !value) {
        // for the case where these have been selected and we want to adjust something later in the journey
        return categories[occupancyType][coverType];
    } if (occupancyType && (!coverType || value)) {
        // for the case where owner type has been selected but we want to change the labels for coverType badges before a coverType
        // has been selected or to apply a different value for each coverType
        return categories[occupancyType][value];
    }
    // Return a sensible default if neither owner or cover type have been selected - in this case use 'owner' as the default
    return categories.OWNER[value];
};

/**
 * Basic selector to get product category string instead of code, normally used for tracking
 * @param {Immutable.Map} state - The application state
 * @returns {String} Product Category text
 */
export const getProductCategory = (state) => {
    const coverType = coverTypeSelector(state);
    const prefix = occupancyTypeSelector(state) === 'LANDLORD' ? 'landlord ' : '';
    switch (coverType) {
        case 'H':
            return `${prefix}Home Cover Only`;
        case 'C':
            return `${prefix}Contents Cover Only`;
        case 'HC':
            return `${prefix}Home & Contents Cover`;
        default:
            return '';
    }
};

/**
 * Selector to effect the opt in text during the Africa promo specific dates / times
 * @param {Immutable.Map} state - The application state
 * @returns {string} The addition opt in text to append to the default text
 */
export const africaPromoOptInText = ({ state }) => {
    let compOptInText = '';
    const serverTime = state.getIn(['page', 'serverTime']);

    const now = new Date(serverTime);
    const compStartTime = new Date('May 14, 2018 09:00:00');
    const compEndTime = new Date('June 30, 2018 00:00:00');

    if (now > compStartTime && now < compEndTime) {
        compOptInText = 'I agree to the <a href="https://secure.comparethemarket.com.au/static/legal/win_a_safari_terms_and_conditions.pdf" target="_blank" '
        + 'data-id="africaPromoOptInTextTermsAndConditionsLink">Terms & Conditions</a> for the "Win a Safari" Promotion that I will be entered into.';
    }

    return compOptInText;
};

/**
 * Gets the shielded payload for the api call
 * @param {Immutable.Map} state - The application Redux store.
 * @param {Object} action - An API action generated via apiAction().
 * @returns {Object} - Payload to send the shielded lead service
 */
export const getShieldedPayload = (state) => {
    const form = quoteFormSelector(state);
    const contactName = form.getIn(['shielded', 'name']);
    const mobile = form.getIn(['shielded', 'mobile']);
    const phone = form.getIn(['shielded', 'phone']);
    const transaction = transactionPayloadSelector(state);
    const brandCode = transaction.get('brandCode');
    const transactionId = transaction.get('journeyId');
    const quoteId = quoteIdSelector(state);
    return {
        brand: brandCode,
        verticalType: 'HOME',
        journeyId: transactionId,
        eventType: 'SHIELDED',
        params: {
            quoteId,
            productId: '0',
            contactName,
            contactNumber: mobile || phone
        }
    };
};

export const pdsDisclaimerAgreement = (state) => {
    const product = state.state.product.get('productMeta').toJS();

    if (!product.features.cvrTyp) {
        return '';
    }

    switch (product.features.cvrTyp.value.trim()) {
        case 'Contents':
            return "By going to the insurer's site you agree you have accessed the Key Facts Sheets for <a>Contents Insurance</a>.";
        case 'Home & Contents':
            return "By going to the insurer's site you agree you have accessed the Key Facts Sheets for <a>Home Insurance</a> and <a>Contents Insurance</a>.";
        case 'Home':
            return "By going to the insurer's site you agree you have accessed the Key Facts Sheets for <a>Home Insurance</a>.";
        default:
            return "By going to the insurer's site you agree you have accessed the Key Facts Sheets for <a>Home Insurance</a> and <a>Contents Insurance</a>.";
    }
};

export const payloadSelectors = {
    shielded: getShieldedPayload
};

/**
 * Get the call centre opening times based on daylight saving
 * It currently returns times based on either AEDT or AEST.
 * @param {Immutable.Map} state - The application state
 * @param {Boolean} isDaylightSavingTimeObserved - True if daylight saving time observed
 * @returns {Object} Returns an object of OpenTime objects for each vertical.
 */
export const getCallCentreOpenHours = ({ state }, isDaylightSavingTimeObserved) => {
    const times = state.getIn(['vertical', 'callCentreHours', 'times']);
    const isDST = (hours) => hours.timeZone === (isDaylightSavingTimeObserved ? 'AEDT' : 'AEST');
    return Object.keys(times).reduce((a, c) => ({ ...a, [c]: times[c].find(isDST) }), {});
};
